import { max, scaleLinear } from 'd3';
import Axis from '../Base/axis';
import Data from '../Base/data';
import Dims from '../Base/dims';
import Margin from '../Base/margin';
import Svg from '../Base/svg';
import Transforms from '../Base/transforms';
import ToolTip from '../ToolTip';
import { ChartCtrParams } from '../types';
import Utils from '../Utils';

export default class Scatter {
  private elementId: string;
  private element: Svg;

  private dims: Dims;
  private margin: Margin;

  private toolTip: ToolTip;
  private useToolTip = false;
  private animators: any;

  public data: Data;
  private _xField = 'x';
  private _yField = 'y';

  public xAxis: Axis;
  public yAxis: Axis;

  public transforms: Transforms;

  /**
   * Constructs a new scatter plot instance
   * ```ts
   * const data = [{ x: 1710, y: 208500 }, ... ];
   * const scatter = new AchoViz.Scatter({ elementId: '#scatter', data });
   * ```
   * @param params - constructor params
   * @returns instance of a ScatterPlot
   */
  constructor(params: ChartCtrParams = {}) {
    this.elementId = params.elementId;
    this.dims = new Dims(params.height, params.width);
    this.margin = new Margin(params.margin);

    this.data = new Data(params.data);
    this._xField = params.xField ?? 'x';
    this._yField = params.yField ?? 'y';

    const xTransform = params.xTransform ?? 'linear';
    const yTransform = params.yTransform ?? 'linear';

    this.transforms = new Transforms({ [this._xField]: xTransform, [this._yField]: yTransform });

    this.yAxis = new Axis({ loc: 'left' });
    this.xAxis = new Axis({ loc: 'bottom' });
    this._useAutoAxis();
  }

  /**
   * Handles drawing, mounting, and rendering the scatter plot
   */
  public render() {
    const { elementId, margin } = this;
    const { height, width } = this.dims.both();

    this.element = new Svg({ elementId, height, width, margin });
    this.element.draw();

    this._drawXAxis();
    this._drawYAxis();
    this._drawPoints();
  }

  /**
   * Draws the plot instance's x axis
   */
  private _drawXAxis() {
    const { d3Svg } = this.element;
    const { height: h } = this.dims.marDims(this.margin);

    const translate = `translate(0, ${h})`;
    const ax = Utils.drawAxis(d3Svg, this.xAxis, { translate });
  }

  /**
   * Draws the plot instance's y axis
   */
  private _drawYAxis() {
    const { d3Svg } = this.element;
    const ax = Utils.drawAxis(d3Svg, this.yAxis);
  }

  /**
   * Draws the points on the plot
   */
  private _drawPoints() {
    const { xAxis, yAxis, _xField, _yField } = this;
    const data = this.data.consume();

    const attr = {
      cx: (d) => xAxis.scale(d[_xField]),
      cy: (d) => yAxis.scale(d[_yField]),
      r: 1.5,
    };
    const styles = {
      fill: 'steelblue',
    };

    Utils.drawCircles(this.element.d3Svg, data, { attr, styles });
  }

  /**
   * Hook that automatically configures the axis scales, domain, and ranges
   */
  private _useAutoAxis() {
    const { _xField, _yField } = this;
    const { height: h, width: w } = this.dims.marDims(this.margin);
    const data = this.data.consume();

    const xMax = max(data, (d) => d[_xField]);
    const xDomain = [0, xMax] as Iterable<any>;
    const xRange = [0, w];
    const xScaleFn = this.transforms.getScale(this._xField);
    // @ts-ignore
    const xScale = xScaleFn().domain(xDomain).range(xRange);
    this.xAxis.setDomain(xDomain);
    this.xAxis.setScale(xScale);

    const yMax = max(data, (d) => d[_yField]);
    const yRange = [h, 0];
    const yDomain = [0, yMax] as Iterable<any>;
    const yScaleFn = this.transforms.getScale(this._yField);

    // @ts-ignore
    const yScale = yScaleFn().domain(yDomain).range(yRange);
    this.yAxis.setDomain(yDomain);
    this.yAxis.setScale(yScale);
  }
}
